import { fetchJSON as fetchAPI } from './client';

export type OrderOpsDashboard = {
  tenant_id: string;
  today_orders: number;
  pending_orders: number;
  late_shipments: number;
  delayed_deliveries: number;
  cancelled_orders: number;
  refunded_orders: number;
  avg_health_score: number;
  operational_sla_pct: number;
  active_exceptions: number;
  active_recommendations: number;
  active_sync_jobs: number;
  generated_at?: string;
};

export type OrderRecord = {
  id: string;
  amazon_order_id: string;
  purchase_date?: string;
  latest_ship_date?: string;
  latest_delivery_date?: string;
  fulfillment_channel: string;
  sales_channel?: string;
  marketplace?: string;
  status: string;
  order_total: number;
  currency?: string;
  is_prime: boolean;
  is_business_order: boolean;
  item_count: number;
  health_score: number;
  fulfillment_score: number;
  delivery_risk_score: number;
  updated_at?: string;
};

export type OrderItemRecord = {
  id: string;
  order_id: string;
  amazon_order_id?: string;
  sku: string;
  asin?: string;
  fnsku?: string;
  title?: string;
  quantity_ordered: number;
  quantity_shipped: number;
  quantity_cancelled: number;
  unit_price: number;
  discount?: number;
};

export type ShipmentRecord = {
  id: string;
  order_id: string;
  amazon_order_id?: string;
  carrier?: string;
  tracking_number?: string;
  shipping_method?: string;
  status: string;
  dispatch_time?: string;
  estimated_delivery?: string;
  actual_delivery?: string;
  transit_days?: number;
};

export type TrackingEventRecord = {
  id: string;
  shipment_id: string;
  tracking_number?: string;
  event: string;
  location?: string;
  occurred_at?: string;
};

export type OrderHealthRecord = {
  id: string;
  order_id: string;
  amazon_order_id?: string;
  health_score: number;
  fulfillment_score: number;
  delivery_risk_score: number;
  cancellation_risk?: number;
  refund_probability?: number;
  issue?: string;
  severity?: string;
};

export type OrderExceptionRecord = {
  id: string;
  order_id: string;
  amazon_order_id?: string;
  type: string;
  description?: string;
  severity?: string;
  resolved: boolean;
};

export type OrderRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  amazon_order_id?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

export type SyncJobRecord = {
  id: string;
  mode: string;
  status: string;
  orders_synced: number;
  orders_failed: number;
  started_at?: string;
  completed_at?: string;
};

export type TimelineEntry = {
  id: string;
  order_id: string;
  amazon_order_id?: string;
  event: string;
  status?: string;
  message?: string;
  occurred_at?: string;
};

const API_BASE = import.meta.env.VITE_ORDEROPS_API ?? '/api/v1/orderops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const orderopsApi = {
  dashboard: () => fetchJSON<OrderOpsDashboard>('/dashboard'),
  listOrders: () => fetchJSON<OrderRecord[]>('/orders'),
  listOrderItems: (orderId?: string) =>
    fetchJSON<OrderItemRecord[]>(orderId ? `/order-items?order_id=${orderId}` : '/order-items'),
  listShipments: () => fetchJSON<ShipmentRecord[]>('/shipments'),
  listTracking: (shipmentId?: string) =>
    fetchJSON<TrackingEventRecord[]>(shipmentId ? `/tracking?shipment_id=${shipmentId}` : '/tracking'),
  listHealth: () => fetchJSON<OrderHealthRecord[]>('/health'),
  listExceptions: () => fetchJSON<OrderExceptionRecord[]>('/exceptions'),
  listRecommendations: () => fetchJSON<OrderRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<OrderRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<SyncJobRecord[]>('/sync-jobs'),
  listTimeline: (orderId?: string) =>
    fetchJSON<TimelineEntry[]>(orderId ? `/timeline?order_id=${orderId}` : '/timeline'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
